import React from "react";
import Button from "../components/Button";
import Label from "../components/Label";
import BasicInput from "../components/BasicInput";
import * as authServices from "../services/authServices";
import i18next from "i18next";
import SimpleDialog from "../components/SimpleDialog";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { setAuthFromApi } from "../state/auth/authSlice";
import toast from "react-hot-toast";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IconButton } from "@mui/material";

type DataType = {
	password: string;
	username: string;
};

export default function LogIn() {
	const [state, setState] = React.useState<DataType>({ password: "", username: "" });
	const [errors, setErrors] = React.useState<DataType>({ password: "", username: "" });
	const [showPassword, setShowPassword] = React.useState<boolean>(false);
	const [loading, setLoading] = React.useState<boolean>(false);

	const navigate = useNavigate();

	const dispatch = useDispatch();

	const goHome = () => navigate("/", { replace: true });

	const validate = () => {
		const errors: any = {};
		if (!state.username) {
			errors.username = i18next.t("username-required");
		}
		if (!state.password) {
			errors.password = i18next.t("password-required");
		}
		setErrors(errors);
		return errors;
	};

	const handleLogin = async (e) => {
		try {
			setLoading(true);
			const errors = validate();
			if (Object.keys(errors).length !== 0) {
				return null;
			}

			const { access_token } = await authServices.login(state.username, state.password);

			if (access_token) {
				dispatch(setAuthFromApi(access_token));
				navigate("/");
				toast.success(i18next.t("login-success"));
			}
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		} finally {
			setLoading(false);
			e.preventDefault();
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleLogin(e);
		}
	};

	return (
		<SimpleDialog open={true} onClose={goHome}>
			<Label isTitle={true} text={i18next.t("login")} />
			<BasicInput
				label={i18next.t("username")}
				value={state.username}
				required={true}
				onChange={(e) => setState({ ...state, username: e.target.value })}
				type="username"
				className="mt-2"
				id="username"
				error={errors.username}
				datatestid="username-input"
				onKeyDown={handleKeyDown}
			/>
			<div className="d-flex align-items-end">
				<BasicInput
					label={i18next.t("password")}
					value={state.password}
					required={true}
					onChange={(e: any) => setState({ ...state, password: e.target.value })}
					type={showPassword ? "text" : "password"}
					id="password"
					className="mt-2 me-2 w-100"
					error={errors.password}
					datatestid="password-input"
					onKeyDown={handleKeyDown}
					inputIconRight={
						<IconButton
							onClick={() => setShowPassword(!showPassword)}
							onMouseDown={(e) => e.preventDefault()}
						>
							{showPassword ? <Visibility fontSize={"small"} /> : <VisibilityOff fontSize={"small"} />}
						</IconButton>
					}
				/>
			</div>

			<div className="d-flex mt-4 justify-content-end">
				<Button label={i18next.t("cancel")} datatestid="login-cancel" onClick={goHome} secondary />
				<Button
					label={i18next.t("accept")}
					loading={loading}
					datatestid="login-accept"
					onClick={handleLogin}
					className="ms-4"
				/>
			</div>
		</SimpleDialog>
	);
}
